import { useState, type KeyboardEvent } from 'react'
import { flushSync } from 'react-dom'
import { MailCheck } from 'lucide-react'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Field, FieldDescription, FieldError, FieldLabel } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import type { PendingSignInEmailChange } from '@/lib/api/types'

export interface SignInEmailActions {
  request(newEmail: string): Promise<PendingSignInEmailChange | null>
  resend(): Promise<PendingSignInEmailChange | null>
  cancel(): Promise<void>
}

interface SignInEmailPanelProps {
  currentEmail: string
  pending: PendingSignInEmailChange | null
  /** Whose sign-in this is, for the wording: your own, or a customer's. */
  who: 'self' | 'customer'
  actions: SignInEmailActions
  /** Keeps ids unique when the panel shows twice on a page. */
  idPrefix: string
  startEditing?: boolean
  initialNewEmail?: string
  onPendingChange?: (pending: PendingSignInEmailChange | null) => void
}

function formatWhen(iso: string) {
  return new Date(iso).toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function apiFieldError(error: unknown) {
  const details = (error as { details?: { field_errors?: Record<string, { message?: string }> } })
    ?.details
  return details?.field_errors?.new_email?.message ?? null
}

/**
 * Changing the email someone signs in with. A link goes to the new address and
 * nothing moves until it is used, so this shows that waiting state with Resend
 * and Cancel. It renders no form of its own, so it can sit inside another one.
 */
export function SignInEmailPanel({
  currentEmail,
  pending: initialPending,
  who,
  actions,
  idPrefix,
  startEditing = false,
  initialNewEmail = '',
  onPendingChange,
}: SignInEmailPanelProps) {
  const [pending, setPending] = useState(initialPending)
  const [editing, setEditing] = useState(startEditing)
  const [newEmail, setNewEmail] = useState(initialNewEmail)
  const [fieldError, setFieldError] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [busy, setBusy] = useState<'send' | 'resend' | 'cancel' | null>(null)
  const inputId = `${idPrefix}-new-sign-in-email`

  function update(next: PendingSignInEmailChange | null) {
    setPending(next)
    onPendingChange?.(next)
  }

  async function run(kind: 'send' | 'resend' | 'cancel', action: () => Promise<void>) {
    setBusy(kind)
    setError(null)
    setFieldError(null)
    setNotice(null)
    try {
      await action()
      setBusy(null)
    } catch (cause) {
      const onField = kind === 'send' ? apiFieldError(cause) : null
      // Re-enable the input first: a disabled input cannot take focus.
      flushSync(() => {
        setBusy(null)
        if (onField) setFieldError(onField)
        else setError(cause instanceof Error ? cause.message : 'Something went wrong. Try again.')
      })
      if (onField) document.getElementById(inputId)?.focus()
    }
  }

  const send = () => {
    const value = newEmail.trim()
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      setFieldError('Enter a valid email address')
      document.getElementById(inputId)?.focus()
      return
    }
    void run('send', async () => {
      update(await actions.request(value))
      setEditing(false)
    })
  }

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    // Inside another form, Enter would submit that one instead.
    if (event.key === 'Enter') {
      event.preventDefault()
      send()
    }
  }

  if (pending) {
    return (
      <Alert role="status">
        <MailCheck className="h-4 w-4" />
        <AlertTitle>Waiting for confirmation at {pending.new_email}</AlertTitle>
        <AlertDescription>
          <p>
            {pending.expired
              ? `The link expired ${formatWhen(pending.expires_at)}. Send a new one, or cancel the change.`
              : `We sent a link to ${pending.new_email}; it works until ${formatWhen(pending.expires_at)}.`}{' '}
            Until it is used, {who === 'self' ? 'you keep' : 'the customer keeps'} signing in with{' '}
            {currentEmail}.
          </p>
          {notice && <p className="mt-1">{notice}</p>}
          {error && (
            <p className="mt-1 text-destructive" role="alert">
              {error}
            </p>
          )}
          <div className="mt-3 flex flex-wrap gap-2">
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={busy !== null}
              onClick={() =>
                run('resend', async () => {
                  update(await actions.resend())
                  setNotice(`Sent again to ${pending.new_email}. Only the newest link works.`)
                })
              }
            >
              {busy === 'resend' ? 'Sending…' : 'Resend link'}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              disabled={busy !== null}
              onClick={() =>
                run('cancel', async () => {
                  await actions.cancel()
                  update(null)
                  setNotice(null)
                })
              }
            >
              {busy === 'cancel' ? 'Cancelling…' : 'Cancel change'}
            </Button>
          </div>
        </AlertDescription>
      </Alert>
    )
  }

  if (!editing) {
    return (
      <div className="space-y-2">
        {error && (
          <p className="text-sm text-destructive" role="alert">
            {error}
          </p>
        )}
        <Button type="button" size="sm" variant="outline" onClick={() => setEditing(true)}>
          Change sign-in email…
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-3 rounded-md border p-3">
      <Field data-invalid={Boolean(fieldError)}>
        <FieldLabel htmlFor={inputId}>New sign-in email</FieldLabel>
        <Input
          id={inputId}
          value={newEmail}
          inputMode="email"
          autoComplete="email"
          onChange={(event) => {
            setNewEmail(event.target.value)
            setFieldError(null)
          }}
          onKeyDown={onKeyDown}
          disabled={busy !== null}
          aria-invalid={Boolean(fieldError) || undefined}
          aria-describedby={`${inputId}-description${fieldError ? ` ${inputId}-error` : ''}`}
        />
        <FieldDescription id={`${inputId}-description`}>
          We email a link to this address. Nothing changes until it is used, within 24 hours.
        </FieldDescription>
        <FieldError id={`${inputId}-error`}>{fieldError}</FieldError>
      </Field>
      {error && (
        <p className="text-sm text-destructive" role="alert">
          {error}
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        <Button type="button" size="sm" onClick={send} disabled={busy !== null}>
          {busy === 'send' ? 'Sending…' : 'Send confirmation link'}
        </Button>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          disabled={busy !== null}
          onClick={() => {
            setEditing(false)
            setFieldError(null)
            setError(null)
          }}
        >
          Cancel
        </Button>
      </div>
    </div>
  )
}
